import React, { useState, useRef } from 'react';

// Comps
import { Link } from '@arctheme-components/elements/Link/Link';

// Styles
import './DropdownMenu.scss';

// Utils
import helpers from 'frontend/js/helpers';

// Lib
import { AnimatePresence, motion } from 'framer-motion';

// Hooks
import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";
import { useClickOutside } from '@arctheme-hooks/useClickOutside';

type Props = {
  links: {
    title: string;
    url: string;
  }[];
  colorSchema?: string;
  hoverAnimation?: boolean;
  disableDesktop?: boolean;
  disableMobile?: boolean;
  breakpoint?: number;
};

const DropdownMenu = ({
  links,
  colorSchema = 'default',
  hoverAnimation,
  disableDesktop,
  disableMobile,
  breakpoint = 1024
}: Props) => {
  // States
  const [openDropdown, setOpenDropdown] = useState(false);

  // Refs
  const activeRef: any = useRef(null);
  const wrapperRef: any = useRef(null);

  const handleClickOutside = () => {
    setOpenDropdown(false);
  };

  // Hooks
  const isMobile = useMobileBreakpoint(`${breakpoint}px`);
  useClickOutside(wrapperRef, handleClickOutside);

  const activeLink = links.find((link) => helpers.isActiveLink(link.url));
  const linksHidden = links.filter((link) => link.url !== activeLink?.url);
  const disabled = (isMobile && disableMobile) || (!isMobile && disableDesktop);

  const renderLinks = (links) => {
    return links.map((link) => {
      const activeStyle = helpers.isActiveLink(link.url)
        ? 'dropdown-menu__item--active b3--bold'
        : 'b3';

      const hoverStyle = hoverAnimation
        ? 'should-color-scheme-hover'
        : '';

      return (
        <li
          key={link.title}
          className='flex'
        >
          <Link
            classes={`dropdown-menu__item ${activeStyle} color-${colorSchema} ${hoverStyle}`}
            link={link.url}
          >
            {link.title}
          </Link>
        </li>
      )
    });
  };

  return (
    <div
      ref={wrapperRef}
      className={`dropdown-menu ${openDropdown ? 'dropdown-menu--active' : disabled ? 'dropdown-menu--disabled' : ''}`}
    >
      <div className='dropdown-menu__inner'>
        {!disabled ? (
          <>
            <div
              ref={activeRef}
              className={`dropdown-menu__active`}
              onClick={() => setOpenDropdown((prev) => !prev)}
            >
              <span>{activeLink && activeLink.title}</span>
              <span className={`icon-chevron-down text-2xl ${openDropdown ? 'rotate-180' : ''} transition-all`}></span>
            </div>

            <AnimatePresence>
              {openDropdown && (
                <motion.ul
                  initial={{ height: 0, overflow: 'hidden' }}
                  animate={{ height: activeRef?.current?.clientHeight * linksHidden?.length, overflow: 'auto' }}
                  exit={{ height: 0 }}
                  transition={{ duration: 0.3 }}
                  className='dropdown-menu__options'
                >
                  {renderLinks(linksHidden)}
                </motion.ul>
              )}
            </AnimatePresence>
          </>
        ) : (
          <ul>{renderLinks(links)}</ul>
        )}
      </div>
    </div>
  );
};

export default DropdownMenu;
